import { AnyAction, combineReducers, configureStore } from '@reduxjs/toolkit';
import authReducer, { logout } from '../core/identity-access/store/authSlice';
import consentReducer from '../core/identity-access/store/consentSlice';
import contextReducer from '../core/identity-access/store/contextSlice';
import coursesReducer from '../verticals/iqmax/store/coursesSlice';
import enrollmentsReducer from '../verticals/iqmax/store/enrollmentsSlice';
import messagesReducer from '../verticals/iqmax/store/messagesSlice';
import notificationsReducer from '../verticals/iqmax/store/notificationsSlice';

const appReducer = combineReducers({
  auth: authReducer,
  consent: consentReducer,
  context: contextReducer,
  courses: coursesReducer,
  enrollments: enrollmentsReducer,
  messages: messagesReducer,
  notifications: notificationsReducer,
});

type AppState = ReturnType<typeof appReducer>;

/**
 * DEC-S2-S01-06 — on a shared device, sign-out clears what the session cached.
 *
 * Every sign-out path ends in `logout`, and sign-out navigates without a reload,
 * so whatever a slice held stayed in the tab for the next person. Reset here
 * rather than per slice: a slice added next sprint is covered without anyone
 * remembering. `auth` keeps its own handling, since its initial state was read
 * from the tokens at load.
 */
export const rootReducer = (state: AppState | undefined, action: AnyAction): AppState =>
  appReducer(logout.match(action) && state ? ({ auth: state.auth } as AppState) : state, action);

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST'],
      },
    }),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
